// src/lib/GuildContext.tsx
// Replaces dash.js's "Guild picker" section. Fetches the owned-guilds
// list from GET /api/guilds (routes/guilds.js — returns guild_id,
// guild_name, settings for guilds the user owns) and tracks which one
// is currently selected across the whole app.
//
// Uses TanStack Query internally (not a raw useEffect, as this file
// originally had) so that other features — like the Channels page's
// settings PATCH — can invalidate the ['guilds'] query key and have
// this context's data refresh automatically, rather than going stale
// after a mutation elsewhere. The exported useGuild() interface is
// unchanged, so Sidebar/TopBar/Overview needed zero changes for this.

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api';

export interface Guild {
  guild_id: string;
  guild_name: string;
  settings: Record<string, unknown>;
}

interface GuildContextValue {
  guilds: Guild[];
  selectedGuildId: string | null;
  setSelectedGuildId: (id: string) => void;
  loading: boolean;
  error: string | null;
}

const GuildContext = createContext<GuildContextValue | undefined>(undefined);

export function GuildProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['guilds'],
    queryFn: () => apiFetch<Guild[]>('/api/guilds'),
  });
  const guilds = data ?? [];

  const [selectedGuildId, setSelectedGuildIdState] = useState<string | null>(null);

  // Restore the last-selected guild if it's still owned, else default
  // to the first — runs whenever the guild list (re)loads, including
  // after a settings-mutation invalidation, not just on initial mount.
  useEffect(() => {
    if (!data) return;
    const saved = localStorage.getItem('selectedGuildId');
    const stillOwned = data.find((g) => g.guild_id === saved);
    setSelectedGuildIdState(stillOwned ? saved : (data[0]?.guild_id ?? null));
  }, [data]);

  function selectGuild(id: string) {
    setSelectedGuildIdState(id);
    localStorage.setItem('selectedGuildId', id);
  }

  return (
    <GuildContext.Provider
      value={{
        guilds,
        selectedGuildId,
        setSelectedGuildId: selectGuild,
        loading: isLoading,
        error: error ? (error as Error).message : null,
      }}
    >
      {children}
    </GuildContext.Provider>
  );
}

export function useGuild(): GuildContextValue {
  const ctx = useContext(GuildContext);
  if (!ctx) throw new Error('useGuild must be used within GuildProvider');
  return ctx;
}
